export function toCurrency(
  number: number | string,
  currency: string = 'USD',
  disableDecimal = false,
  decimalPlaces = 2
) {
  // Validate and normalize currency
  const normalizedCurrency = currency?.toUpperCase() || 'USD';

  // Get the appropriate locale based on the currency
  const locale = getLocaleForCurrency(normalizedCurrency);

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: disableDecimal ? 0 : decimalPlaces,
      maximumFractionDigits: disableDecimal ? 0 : decimalPlaces,
    });
    return formatter.format(+number);
  } catch (error) {
    // Fallback to USD if currency formatting fails
    const fallbackFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: disableDecimal ? 0 : decimalPlaces,
      maximumFractionDigits: disableDecimal ? 0 : decimalPlaces,
    });
    return fallbackFormatter.format(+number);
  }
}

// Helper function to get appropriate locale for currency
function getLocaleForCurrency(currency: string): string {
  const currencyLocaleMap: { [key: string]: string } = {
    TRY: 'tr-TR',
    EUR: 'de-DE',
    GBP: 'en-GB',
    JPY: 'ja-JP',
    CNY: 'zh-CN',
    INR: 'en-IN',
    RUB: 'ru-RU',
    BRL: 'pt-BR',
    KRW: 'ko-KR',
    MXN: 'es-MX',
    CAD: 'en-CA',
    AUD: 'en-AU',
    CHF: 'de-CH',
    SEK: 'sv-SE',
    NOK: 'nb-NO',
    DKK: 'da-DK',
    PLN: 'pl-PL',
    HUF: 'hu-HU',
    CZK: 'cs-CZ',
    ILS: 'he-IL',
    ZAR: 'en-ZA',
    SGD: 'en-SG',
    NZD: 'en-NZ',
    HKD: 'zh-HK',
    TWD: 'zh-TW',
    THB: 'th-TH',
    IDR: 'id-ID',
    MYR: 'ms-MY',
    PHP: 'en-PH',
    VND: 'vi-VN',
    AED: 'ar-AE',
    SAR: 'ar-SA',
    QAR: 'ar-QA',
    KWD: 'ar-KW',
    BHD: 'ar-BH',
    OMR: 'ar-OM',
    JOD: 'ar-JO',
    LBP: 'ar-LB',
    EGP: 'ar-EG',
    MAD: 'ar-MA',
    DZD: 'ar-DZ',
    TND: 'ar-TN',
    LYD: 'ar-LY',
    SDG: 'ar-SD',
    IQD: 'ar-IQ',
    SYP: 'ar-SY',
    YER: 'ar-YE',
    AFN: 'ps-AF',
    PKR: 'ur-PK',
    BDT: 'bn-BD',
    LKR: 'si-LK',
    NPR: 'ne-NP',
    BTN: 'dz-BT',
    MMK: 'my-MM',
    KHR: 'km-KH',
    LAK: 'lo-LA',
    MNT: 'mn-MN',
    UZS: 'uz-UZ',
    KZT: 'kk-KZ',
    GEL: 'ka-GE',
    AMD: 'hy-AM',
    AZN: 'az-AZ',
    BYN: 'be-BY',
    MDL: 'ro-MD',
    UAH: 'uk-UA',
    RSD: 'sr-RS',
    HRK: 'hr-HR',
    BGN: 'bg-BG',
    RON: 'ro-RO',
    ISK: 'is-IS',
    ALL: 'sq-AL',
    MKD: 'mk-MK',
    BAM: 'bs-BA',
    MUR: 'en-MU',
    SCR: 'en-SC',
    MVR: 'dv-MV',
    NIO: 'es-NI',
    PAB: 'es-PA',
    COP: 'es-CO',
    CLP: 'es-CL',
    ARS: 'es-AR',
    PEN: 'es-PE',
    BOB: 'es-BO',
    PYG: 'es-PY',
    UYU: 'es-UY',
    VES: 'es-VE',
    CRC: 'es-CR',
    DOP: 'es-DO',
    GTQ: 'es-GT',
    HNL: 'es-HN',
    SVC: 'es-SV',
    JMD: 'en-JM',
    TTD: 'en-TT',
    BBD: 'en-BB',
    BZD: 'en-BZ',
    GYD: 'en-GY',
    SRD: 'nl-SR',
    ANG: 'nl-CW',
    AWG: 'nl-AW',
    XCD: 'en-GD',
    KYD: 'en-KY',
    BSD: 'en-BS',
    BMD: 'en-BM',
    FJD: 'en-FJ',
    SBD: 'en-SB',
    TOP: 'to-TO',
    WST: 'sm-WS',
    VUV: 'bi-VU',
    PGK: 'en-PG',
    SLL: 'en-SL',
    GMD: 'en-GM',
    GHS: 'en-GH',
    NGN: 'en-NG',
    XOF: 'fr-SN',
    XAF: 'fr-CM',
    CDF: 'fr-CD',
    RWF: 'rw-RW',
    BIF: 'rn-BI',
    KMF: 'ar-KM',
    DJF: 'fr-DJ',
    ERN: 'ti-ER',
    ETB: 'am-ET',
    SOS: 'so-SO',
    TZS: 'sw-TZ',
    UGX: 'sw-UG',
    KES: 'sw-KE',
    MWK: 'ny-MW',
    ZMW: 'en-ZM',
    MZN: 'pt-MZ',
    ZWL: 'en-ZW',
    NAD: 'en-NA',
    SSP: 'en-SS',
    SZL: 'en-SZ',
    LSL: 'st-LS',
    BWP: 'en-BW',
    MGA: 'mg-MG',
    CVE: 'pt-CV',
    STN: 'pt-ST',
  };

  return currencyLocaleMap[currency.toUpperCase()] || 'en-US';
}
